// CrmAddContact — the single "Add contact" entry point for the Contacts list.
// Button -> dropdown: Create contact (manual form w/ company picker + LinkedIn
// prefill) or Import contacts (CSV/Excel modal). Mirrors the company-card
// create flow exactly (POST /companies/:companyId/contacts).
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import CrmImportModal from './CrmImportModal';

export default function CrmAddContact({ clientId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const menuRef = useRef(null);

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [form, setForm] = useState({ linkedin_url: '', full_name: '', job_title: '', email: '', photo_url: '' });
  const [fetchingLi, setFetchingLi] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    function onDoc(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  async function loadCompanies() {
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/companies`);
      const d = await r.json();
      setCompanies(Array.isArray(d.rows) ? d.rows : (Array.isArray(d) ? d : []));
    } catch (_) { setCompanies([]); }
  }

  function openCreate() {
    setForm({ linkedin_url: '', full_name: '', job_title: '', email: '', photo_url: '' });
    setCompanyId(''); setCompanyQuery('');
    setOpen(false); setMode('create');
    loadCompanies();
  }
  function openImport() { setOpen(false); setMode('import'); }
  function close() { setMode(null); }

  async function fetchFromLinkedin() {
    const url = form.linkedin_url.trim();
    if (!url.toLowerCase().includes('linkedin.com/in/')) { toast.error('Enter a valid LinkedIn profile URL'); return; }
    setFetchingLi(true);
    const tId = toast.loading('Reading LinkedIn profile…');
    try {
      const r = await fetch(`${API_BASE}/parse-linkedin`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ linkedin_url: url }) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || 'Could not read that profile', { id: tId }); }
      else if (d.found) { setForm(p => ({ ...p, full_name: d.full_name || p.full_name, job_title: d.job_title || p.job_title, photo_url: d.photo_url || p.photo_url })); toast.success('Prefilled from LinkedIn', { id: tId }); }
      else { toast('No details found — fill the form manually', { id: tId }); }
    } catch (e) { toast.error('Lookup failed', { id: tId }); }
    setFetchingLi(false);
  }

  async function createContact() {
    if (!companyId) { toast.error('Choose a company'); return; }
    if (!form.full_name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API_BASE}/companies/${companyId}/contacts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, full_name: form.full_name.trim(), job_title: form.job_title.trim(), email: form.email.trim(), linkedin_url: form.linkedin_url.trim(), photo_url: form.photo_url || '' })
      });
      const d = await r.json();
      if (r.status === 409) { toast.error(d.message || 'That contact already exists'); setCreating(false); return; }
      if (!r.ok) { toast.error(d.error || 'Could not create contact'); setCreating(false); return; }
      toast.success('Contact added');
      setMode(null);
      if (onCreated) onCreated();
    } catch (e) { toast.error('Could not create contact'); }
    setCreating(false);
  }

  const filteredCompanies = companyQuery.trim()
    ? companies.filter(c => (c.name || '').toLowerCase().includes(companyQuery.trim().toLowerCase()))
    : companies;
  const selectedCompany = companies.find(c => String(c.id) === String(companyId));

  return (
    <div className="crmadd" ref={menuRef}>
      <button className="crmadd-btn" type="button" onClick={() => setOpen(v => !v)}>
        <span className="crmadd-btn-ic">+</span>Add contact
        <span className="crmadd-btn-caret">▾</span>
      </button>

      {open && (
        <div className="crmadd-menu">
          <button className="crmadd-menu-item" onClick={openCreate}>
            <span className="crmadd-menu-ic">✎</span>
            <div><div className="crmadd-menu-l">Create contact</div><div className="crmadd-menu-s">Add one by hand, with LinkedIn prefill</div></div>
          </button>
          <button className="crmadd-menu-item" onClick={openImport}>
            <span className="crmadd-menu-ic">↑</span>
            <div><div className="crmadd-menu-l">Import contacts</div><div className="crmadd-menu-s">Bulk upload from CSV or Excel</div></div>
          </button>
        </div>
      )}

      {mode === 'import' && (
        <CrmImportModal clientId={clientId} onClose={close} onComplete={() => { close(); if (onCreated) onCreated(); }} />
      )}

      {mode === 'create' && (
        <div className="crmadd-overlay" onClick={() => !creating && !fetchingLi && close()}>
          <div className="crmadd-modal" onClick={e => e.stopPropagation()}>
            <div className="crmadd-head">
              <h2 className="crmadd-title">Create contact</h2>
              <button className="crmadd-x" onClick={close} title="Close">×</button>
            </div>
            <p className="crmadd-sub">Paste a LinkedIn profile to prefill, or enter details by hand. Choose the company this contact belongs to.</p>

            <label className="crmadd-label">Company *</label>
            {selectedCompany ? (
              <div className="crmadd-company-sel">
                <span>{selectedCompany.name}</span>
                <button className="crmadd-company-clear" onClick={() => { setCompanyId(''); setCompanyQuery(''); }} title="Change">×</button>
              </div>
            ) : (
              <>
                <input className="crmadd-input" placeholder="Search companies…" value={companyQuery} onChange={e => setCompanyQuery(e.target.value)} />
                {companyQuery.trim() && (
                  <div className="crmadd-company-list">
                    {filteredCompanies.length === 0 ? <div className="crmadd-company-empty">No matches</div>
                      : filteredCompanies.slice(0, 8).map(c => (
                        <button key={c.id} className="crmadd-company-opt" onClick={() => { setCompanyId(c.id); setCompanyQuery(''); }}>{c.name}</button>
                      ))}
                  </div>
                )}
              </>
            )}

            <label className="crmadd-label">LinkedIn URL</label>
            <div className="crmadd-li">
              <input className="crmadd-input" placeholder="linkedin.com/in/…" value={form.linkedin_url} onChange={e => setForm({ ...form, linkedin_url: e.target.value })} />
              <button className="crmadd-li-btn" disabled={fetchingLi} onClick={fetchFromLinkedin}>{fetchingLi ? 'Reading…' : 'Prefill'}</button>
            </div>

            <label className="crmadd-label">Full name *</label>
            <input className="crmadd-input" placeholder="e.g. Jane Smith" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />

            <div className="crmadd-row">
              <div>
                <label className="crmadd-label">Job title</label>
                <input className="crmadd-input" placeholder="e.g. Head of Sales" value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} />
              </div>
              <div>
                <label className="crmadd-label">Email</label>
                <input className="crmadd-input" placeholder="name@company.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>

            <div className="crmadd-actions">
              <button className="crmadd-act crmadd-act-ghost" onClick={close} disabled={creating}>Cancel</button>
              <button className="crmadd-act crmadd-act-primary" onClick={createContact} disabled={creating || !companyId || !form.full_name.trim()}>{creating ? 'Adding…' : 'Add contact'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
